import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import CatalogFormModal from './CatalogFormModal';

const PAGE_SIZE = 30;

/* ── Helpers ──────────────────────────────────────── */
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

/** Returns an array of page numbers + '...' placeholders */
function getPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
}

/* ── Small icons (16×16) ─────────────────────────── */
function IconBack() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function IconDeactivate() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function IconRestore() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconReadOnly() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/* ── Component ────────────────────────────────────── */
function CatalogCRUD({ config, canEdit, onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [config.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  async function fetchData() {
    setLoading(true);
    const { data } = await supabase
      .from(config.table)
      .select(config.select || '*')
      .order('active', { ascending: false })
      .order(config.orderBy || 'name');
    setRows(data || []);
    setLoading(false);
  }

  async function handleDeactivate() {
    if (!confirmRow) return;
    setActionLoading(true);
    await supabase
      .from(config.table)
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', confirmRow.id);
    setConfirmRow(null);
    setActionLoading(false);
    fetchData();
  }

  async function handleReactivate(row) {
    await supabase
      .from(config.table)
      .update({ active: true, updated_at: new Date().toISOString() })
      .eq('id', row.id);
    fetchData();
  }

  async function handleSave(formData) {
    const processed = {};
    config.fields.forEach((field) => {
      let val = formData[field.key];
      if ((field.type === 'fk' || field.type === 'number') && val !== '' && val != null) {
        val = Number(val);
      }
      if (!field.required && (val === '' || val === undefined)) {
        val = null;
      }
      processed[field.key] = val;
    });

    if (editingRow) {
      processed.updated_at = new Date().toISOString();
      const { error } = await supabase
        .from(config.table)
        .update(processed)
        .eq('id', editingRow.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from(config.table)
        .insert(processed);
      if (error) throw error;
    }

    setModalOpen(false);
    setEditingRow(null);
    fetchData();
  }

  /* ── Derived data ── */
  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return config.columns.some((col) => {
      const val = getNestedValue(row, col.key);
      return String(val || '').toLowerCase().includes(s);
    });
  });

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageList = getPageList(safePage, totalPages);

  /* ── Count label ── */
  function countLabel() {
    if (search.trim() && filteredRows.length !== rows.length) {
      return `Se encontraron ${filteredRows.length} de ${rows.length} registros`;
    }
    return `Se encontraron ${rows.length} registro${rows.length !== 1 ? 's' : ''}`;
  }

  /* ── Cell rendering ── */
  function renderCell(col, row) {
    const val = getNestedValue(row, col.key);
    if (col.key === 'color' && val) {
      return (
        <span className="cat-color-cell">
          <span className="cat-color-swatch" style={{ background: val }} />
          {val}
        </span>
      );
    }
    if (col.format) return col.format(val);
    return val ?? '—';
  }

  return (
    <>
    <div className="cat-crud">
      {/* ── Header ── */}
      <div className="cat-crud-header">
        <button className="cat-back-btn" onClick={onBack}>
          <IconBack />
          <span>Regresar</span>
        </button>
        <h1 className="cat-crud-title">{config.label}</h1>
        {canEdit && (
          <button
            className="cat-new-btn"
            onClick={() => { setEditingRow(null); setModalOpen(true); }}
          >
            <IconPlus />
            <span>Nuevo</span>
          </button>
        )}
      </div>

      {/* ── Read-only notice for Líder ── */}
      {!canEdit && (
        <div className="cat-readonly-banner">
          <IconReadOnly />
          Solo lectura — únicamente el Gerente puede crear o modificar catálogos.
        </div>
      )}

      {/* ── Search + count bar ── */}
      <div className="cat-toolbar">
        <div className="cat-search-wrap">
          <IconSearch />
          <input
            type="text"
            className="cat-search"
            placeholder="Buscar en este catálogo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {!loading && (
          <span className="cat-count">{countLabel()}</span>
        )}
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="cat-loading">Cargando registros…</div>
      ) : filteredRows.length === 0 ? (
        <div className="cat-empty">
          {search ? 'No se encontraron resultados.' : 'No hay registros en este catálogo.'}
        </div>
      ) : (
        <>
          <div className="cat-table-wrap">
            <table className="cat-table">
              <thead>
                <tr>
                  {config.columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                  <th>Estado</th>
                  {canEdit && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => (
                  <tr key={row.id} className={row.active ? '' : 'cat-row--inactive'}>
                    {config.columns.map((col) => (
                      <td key={col.key}>{renderCell(col, row)}</td>
                    ))}
                    <td>
                      <span className={`cat-badge ${row.active ? 'cat-badge--active' : 'cat-badge--inactive'}`}>
                        {row.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    {canEdit && (
                      <td>
                        <div className="cat-actions">
                          <button
                            className="cat-action-btn cat-action-edit"
                            onClick={() => { setEditingRow(row); setModalOpen(true); }}
                            title="Editar"
                          >
                            <IconEdit />
                          </button>
                          {row.active ? (
                            <button
                              className="cat-action-btn cat-action-delete"
                              onClick={() => setConfirmRow(row)}
                              title="Desactivar"
                            >
                              <IconDeactivate />
                            </button>
                          ) : (
                            <button
                              className="cat-action-btn cat-action-restore"
                              onClick={() => handleReactivate(row)}
                              title="Reactivar"
                            >
                              <IconRestore />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="cat-pagination-wrap">
              <span className="cat-page-info">
                Página {safePage} de {totalPages}
              </span>
              <div className="cat-pagination">
                <button
                  className="cat-page-nav"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  aria-label="Página anterior"
                >
                  <IconChevronLeft />
                </button>

                {pageList.map((item, i) =>
                  item === '...' ? (
                    <span key={`ellipsis-${i}`} className="cat-page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`cat-page-btn${safePage === item ? ' cat-page-btn--active' : ''}`}
                      onClick={() => setPage(item)}
                      aria-current={safePage === item ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  )
                )}

                <button
                  className="cat-page-nav"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  aria-label="Página siguiente"
                >
                  <IconChevronRight />
                </button>
              </div>
            </div>
          )}
        </>
      )}

    </div>

    {/* ── Form modal (rendered outside .cat-crud so its fixed positioning
         resolves against the viewport, not the animated wrapper's
         transform containing block) ── */}
    {modalOpen && (
      <CatalogFormModal
        config={config}
        row={editingRow}
        onSave={handleSave}
        onClose={() => { setModalOpen(false); setEditingRow(null); }}
      />
    )}

    {/* ── Deactivate confirmation ── */}
    {confirmRow && (
      <>
        <div className="cat-backdrop" onClick={() => setConfirmRow(null)} aria-hidden="true" />
        <div className="cat-confirm" role="alertdialog" aria-modal="true">
          <h3 className="cat-confirm-title">¿Desactivar {config.singular}?</h3>
          <p className="cat-confirm-msg">
            Este registro será marcado como inactivo y no aparecerá en los formularios del sistema. Podrás reactivarlo después.
          </p>
          <div className="cat-confirm-actions">
            <button
              className="cat-btn-cancel"
              onClick={() => setConfirmRow(null)}
              disabled={actionLoading}
            >
              Cancelar
            </button>
            <button
              className="cat-btn-danger"
              onClick={handleDeactivate}
              disabled={actionLoading}
            >
              {actionLoading ? 'Desactivando…' : 'Desactivar'}
            </button>
          </div>
        </div>
      </>
    )}
    </>
  );
}

export default CatalogCRUD;
